import React, { SyntheticEvent, useState } from 'react';
import { connect } from 'react-redux';
import flow from 'lodash/fp/flow';
import SquareButton from '../Buttons/SquareButton';
import { TrialUserCopyright, LicensedUserCopyright } from './CopyrightContent';
import maybe from '../enhancers/maybe';
import CheckBox from '../../@common/CheckBox/CheckBox';
import { remCalc } from '../../../util/style-util';
import { translate } from '../../../translations/translator';
import { Workbook, ProductCopyrightTerms, AppStoreState } from 'types';
import { UserState } from 'state/modules/user';
import { saveCopyrightTerms } from 'state/modules/user/thunks';
import { FONT_GSS_FUTURA } from 'themes/default';
import Overlay from '../ScreenOverlay';
import { makeStyles } from '@mui/styles';
const translateCopyright = key => translate('copyright', key);

type Props =
  | {
      handleDismiss: (e: SyntheticEvent<HTMLElement>) => void;
      currentWorkbook: Workbook;
      user: UserState;
      saveCopyrightTerms: (productCopyrightTerms: ProductCopyrightTerms) => void;
      showCheckbox: boolean;
      isTrialLicense: boolean;
    }
  | any;

const MaybeCheckbox: any = maybe(CheckBox);

const CopyrightModal = (props: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = useState(false);

  const handleCheck = () => setChecked(!checked);

  const handleSubmit = (e: SyntheticEvent<HTMLElement>) => {
    const { currentWorkbook, handleDismiss, saveCopyrightTerms, showCheckbox } = props;
    const action = showCheckbox ? 'accepted' : 'viewed';
    const [{ productCurriculumId }] = currentWorkbook.products;

    if (checked || !showCheckbox) {
      saveCopyrightTerms({ productCurriculumId, [action]: new Date().toISOString() });

      handleDismiss && handleDismiss(e);
    }
  };

  const { showCheckbox, isTrialLicense, currentWorkbook }: any = props;
  return (
    <Overlay zIndex={11} show={true}>
      <div className={classes.copyrightModal}>
        <div className={classes.modalHeader}>{translateCopyright('modalHeader')}</div>
        {isTrialLicense ? (
          <TrialUserCopyright productTitle={currentWorkbook.title} />
        ) : (
          <LicensedUserCopyright productTitle={currentWorkbook.title} />
        )}
        <hr className={classes.divider} />
        <div className={classes.modalFooter}>
          <MaybeCheckbox
            shouldRender={showCheckbox}
            label={translateCopyright('checkbox')}
            checked={checked}
            onChange={handleCheck}
            role={'checkbox'}
            aria-checked={checked}
            aria-label={translateCopyright('checkboxTag')}
          />
          <SquareButton
            buttonStyles={[classes.modalButton]}
            text={showCheckbox ? translateCopyright('submit') : translateCopyright('gotIt')}
            onClick={handleSubmit}
            disabled={showCheckbox ? !checked : false}
            aria-label={translateCopyright('submitTag')}
          />
        </div>
      </div>
    </Overlay>
  );
};
const modalTop = window.innerHeight > 580 ? (window.innerHeight - 580) / 2 : 0;
const useStyles = makeStyles(() => ({
  copyrightModal: {
    padding: '30px',
    position: 'fixed',
    top: modalTop,
    backgroundColor: '#FFFFFF',
    textAlign: 'center',
    maxWidth: remCalc(250),
    '@media(min-width: 1000px)': {
      maxWidth: remCalc(500)
    }
  },
  modalButton: {
    margin: remCalc(20, 0)
  },
  divider: {
    margin: remCalc(30, 0, 10, 0),
    border: '1px solid black'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'center'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'center',
    textDecoration: 'underline',
    fontSize: '30px',
    fontWeight: 700,
    marginBottom: '10px',
    fontFamily: FONT_GSS_FUTURA
  }
}));

const mapStateToProps = (state: AppStoreState) => ({
  user: state.user
});

const withRedux = connect(mapStateToProps, {
  saveCopyrightTerms
});

export default flow(withRedux)(CopyrightModal);
